import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService, TwoFactorSetupData } from '../../authentication.service';
import { HTTP_STATUS_OFFLINE } from '../../../http/httpError';
import { LoadingDirective } from '@styleguide/loading/loading.directive';
import { ErrorsComponent } from '@styleguide/error/errors/errors.component';
import { FormFieldComponent } from '@styleguide/form/form-field/form-field.component';
import { CheckboxControlComponent } from '@styleguide/form/checkbox-control/checkbox-control.component';
import { BackupCodesComponent } from '../backup-codes/backup-codes.component';
import { CodeInputComponent } from '../code-input/code-input.component';

enum SetupStep {
  Loading = 'loading',
  ScanAndVerify = 'scan',
  BackupCodes = 'backup',
}

@Component({
  selector: 'kgr-two-factor-setup',
  templateUrl: './two-factor-setup.component.html',
  styleUrl: './two-factor-setup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingDirective,
    ReactiveFormsModule,
    ErrorsComponent,
    FormFieldComponent,
    CheckboxControlComponent,
    BackupCodesComponent,
    CodeInputComponent,
  ],
})
export class TwoFactorSetupComponent implements OnInit {
  @Input({ required: true }) loginContextId!: number;
  @Input({ required: true }) authToken!: string;
  @Input() mandatory = false;
  @Output() setupComplete = new EventEmitter<void>();

  readonly SetupStep = SetupStep;

  step = SetupStep.Loading;
  isLoading = false;
  setupData: TwoFactorSetupData | null = null;
  backupCodes: string[] = [];

  errorInvalidCode = false;
  errorOffline = false;
  unexpectedError = false;
  secretCopied = false;
  backupCodesConfirmed = new FormControl(false);

  codeForm = new UntypedFormGroup({
    code: new FormControl<string>('', [Validators.required, Validators.minLength(6), Validators.maxLength(8)]),
  });

  constructor(
    private authenticationService: AuthenticationService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.startSetup();
  }

  private startSetup(): void {
    this.isLoading = true;
    this.authenticationService.start2FASetup(this.loginContextId, this.authToken).subscribe({
      next: (data) => {
        this.setupData = data;
        this.step = SetupStep.ScanAndVerify;
        this.isLoading = false;
        this.cd.markForCheck();
      },
      error: (apiError) => {
        this.handleError(apiError);
      },
    });
  }

  onVerifyCode(): void {
    if (this.codeForm.invalid) {
      return;
    }
    this.resetErrors();
    this.isLoading = true;
    this.authenticationService.verify2FASetup(this.loginContextId, this.codeForm.value.code, this.authToken).subscribe({
      next: () => {
        this.generateBackupCodes();
      },
      error: (apiError) => {
        if (apiError.code === 400) {
          this.errorInvalidCode = true;
        } else {
          this.handleError(apiError);
        }
        this.isLoading = false;
        this.cd.markForCheck();
      },
    });
  }

  private generateBackupCodes(): void {
    this.authenticationService.generate2FABackupCodes(this.loginContextId, this.authToken).subscribe({
      next: (codes) => {
        this.backupCodes = codes;
        this.step = SetupStep.BackupCodes;
        this.isLoading = false;
        this.cd.markForCheck();
      },
      error: (apiError) => {
        this.handleError(apiError);
      },
    });
  }

  copySecret(): void {
    navigator.clipboard.writeText(this.setupData!.setup.secret).then(() => {
      this.secretCopied = true;
      this.cd.markForCheck();
      setTimeout(() => {
        this.secretCopied = false;
        this.cd.markForCheck();
      }, 2000);
    });
  }

  onSetupComplete(): void {
    this.setupComplete.emit();
  }

  private handleError(apiError: any): void {
    switch (apiError.code) {
      case HTTP_STATUS_OFFLINE:
      case 0:
        this.errorOffline = true;
        break;
      default:
        this.unexpectedError = true;
        break;
    }
    this.isLoading = false;
    this.cd.markForCheck();
  }

  private resetErrors(): void {
    this.errorInvalidCode = false;
    this.errorOffline = false;
    this.unexpectedError = false;
  }
}
